// @ts-check
// =============================================================================
// js/screens/duel/hand.js — your hand, as a horizontal PIXI scroller.
//
// WHAT THIS IS
//   The port of CardsInHand (:3881) + the handScroller DOM strip. Each card
//   is a full PixiCard from the preserved generator, scaled to the hand row.
//   The row drag-scrolls when it overflows, cards click to select
//   (targeting.js) and press-hold to zoom.
//
// PSEUDO-REACT RENDERING (see core/reconcile.js)
//   Cards are a KEYED LIST: each mounted cell carries the key of the card it
//   renders (id + art + the stat fields that affect pixels). A new state is
//   diffKeyed()-matched against the mounted cells:
//     kept    -> the SAME container survives; it just slides to its new slot
//     removed -> fades out and is destroyed (the card you played)
//     added   -> built async and pops in (the card you drew)
//   So drawing one card renders ONE card; the rest of the hand never blinks.
//   A full mount happens only on first render / layout change (duel.js clears
//   scene.handCells to force it).
//
//   When it's the opponent's view (duel.player1.hand is a NUMBER — the server
//   strips hidden info) no hand renders; the count lives in the resource bar.
// =============================================================================

import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { gen, resolveCardArt } from '../../render/card-tile.js';
import { diffKeyed } from '../../core/reconcile.js';
import { highlightFor, grayFor, click, targeting } from './targeting.js';
import { HIGHLIGHT_COLORS, applyCardGrayscale, gentlePulse, killPulse } from './board.js';
import { applyHighlight } from './highlight-fx.js';

/** Native size of a generator card container (frame art 778×1082). */
const CARD_NATIVE_W = 778;
const CARD_NATIVE_H = 1082;

/**
 * Everything that affects a hand card's pixels. Equal key = reuse the
 * mounted container as-is. (resolveCardArt must run before keying so the art
 * choice is part of the identity.)
 * @param {any} card
 * @returns {string}
 */
function cardKey(card) {
	return [
		card.id, card.selectedArt || '', card.cropCost, card.meatCost,
		card.waitCost, card.attack, card.health,
	].join('|');
}

/**
 * Entry point: build or reconcile the hand for a new duel state.
 * @param {object} scene   The duel scene.
 * @param {any} duel       The current duel snapshot.
 * @returns {Promise<void>}
 */
export async function renderHand(scene, duel) {
	const hidden = !duel || typeof duel.player1.hand === 'number';
	if (hidden) {
		scene.layers.hand.removeChildren();
		scene.handCells = null;
		scene.handMount = null;
		scene.handCards = [];
		return;
	}
	// The deal-in tween belongs to genuinely NEW cards only. On a REMOUNT of
	// a duel we've already dealt (layout change, resize, the focus-return
	// viewport dance) every card takes the "added" path — those place
	// INSTANTLY instead of replaying the whole draw animation.
	const freshMount = !scene.handMount;
	if (freshMount) {
		mountHandStrip(scene);
	}
	const instant = freshMount && dealtHandFor === duel.id;
	dealtHandFor = duel.id;
	await reconcileHand(scene, duel.player1.hand, instant);
}

/** Which duel's hand has already had its opening deal animated. */
let dealtHandFor = /** @type {string|null} */ (null);

/**
 * One-time strip/mask/drag scaffolding (per mount/layout).
 * @param {object} scene
 */
function mountHandStrip(scene) {
	const layer = scene.layers.hand;
	layer.removeChildren();

	const { handY, handH, width } = scene.metrics;
	const cardH = handH;
	const cardW = cardH * (CARD_NATIVE_W / CARD_NATIVE_H);
	// The rise height is a LAYOUT metric: the headroom computeMetrics reserved
	// between the bottom bar and the hand (minus a hair), so a risen card
	// never slides under the buttons above.
	const lift = Math.max(8, (scene.metrics.handLiftRoom || 0) + 2);

	// The scrolling strip sits inside a mask so cards don't spill the screen.
	// The mask reaches above the row so the selected card can LIFT visibly.
	const strip = new PIXI.Container();
	// Cards may overlap (fan layout) — stack left-under-right by zIndex, and
	// let the selected card jump the queue (applyHandTargeting).
	strip.sortableChildren = true;
	const mask = new PIXI.Graphics().rect(0, handY - lift - 8, width, handH + lift + 38).fill(0xffffff);
	layer.addChild(mask);
	strip.mask = mask;
	layer.addChild(strip);

	scene.handMount = {
		strip, cardW, cardH, lift, handY,
		viewW: width,
		totalW: 0, // kept current by reconcileHand; read live by the drag clamp
	};
	scene.handCells = [];
	enableDragScroll(scene, strip);
}

/**
 * The fan geometry for a hand of `count` cards: cards sit CENTERED and always
 * touching — the overlap is at least 5% of a card (no gaps between cards)
 * and at most 50% (every card keeps half its width visible); beyond that the
 * row overflows into drag-scrolling.
 * Exported for animations.js (refanHandWithout closes the gap the moment a
 * played card lifts out, before the server state even lands).
 * @param {object} scene
 * @param {number} count
 * @returns {{pitch:number, totalW:number, startX:number, slotX:(i:number)=>number}}
 */
export function fanLayout(scene, count) {
	const { cardW } = scene.handMount;
	const availW = scene.metrics.width - 20;
	const idealPitch = cardW * 0.95; // minimum 5% overlap — the hand stays together
	const fitPitch = count > 1 ? (availW - cardW) / (count - 1) : idealPitch;
	const pitch = Math.max(Math.min(idealPitch, fitPitch), cardW / 2);
	const totalW = cardW + Math.max(0, count - 1) * pitch;
	const startX = Math.max((scene.metrics.width - totalW) / 2, 10);
	return { pitch, totalW, startX, slotX: (i) => startX + i * pitch };
}

/**
 * Keyed reconcile: bring the mounted cells in line with the new hand.
 * @param {object} scene
 * @param {any[]} hand
 * @param {boolean} [instant]  Remount of an already-dealt hand: place added
 *   cards at their slots with no deal-in tween.
 */
async function reconcileHand(scene, hand, instant = false) {
	const mount = scene.handMount;
	const { cardW, cardH, handY } = mount;

	for (const card of hand) resolveCardArt(card, true);
	const keys = hand.map(cardKey);
	const { kept, added, removed } = diffKeyed(scene.handCells, keys);

	// New row geometry (count may have changed) — see fanLayout.
	const { pitch, totalW, slotX } = fanLayout(scene, hand.length);
	mount.pitch = pitch; // the selection swoosh derives the fan overlap from it
	mount.totalW = totalW;

	/** @type {any[]} the next mounted list, in hand order */
	const nextCells = new Array(hand.length);
	/** @type {Promise<void>[]} */
	const builds = [];

	// KEPT: same container — retarget its spot/data and slide to its slot.
	for (const { entry, newIndex } of kept) {
		entry.spot = `Card_${newIndex}`;
		entry.data = hand[newIndex];
		entry.index = newIndex;
		entry.baseX = slotX(newIndex);
		nextCells[newIndex] = entry;
		entry.cell.zIndex = newIndex; // fan stacking: left under right
		// A play-flight may have hidden this cell and flown its render away
		// (duplicate copies share a key) — bring it back whole.
		entry.cell.visible = true;
		if (entry.cell.children.length < 2) {
			builds.push(buildCardRender(scene, entry));
		}
		gsap.killTweensOf(entry.cell, 'x');
		gsap.to(entry.cell, { x: entry.baseX, duration: 0.22, ease: 'power2.out' });
	}

	// REMOVED: the card you just played — fade out, then destroy.
	for (const entry of removed) {
		gsap.killTweensOf(entry.cell);
		gsap.to(entry.cell, {
			alpha: 0, y: entry.baseY + 20, duration: 0.18, ease: 'power1.in',
			onComplete: () => entry.cell.destroy({ children: true }),
		});
	}

	// ADDED: the card you just drew — it tweens IN from the Draw chip (scaling
	// up as it arrives) while the kept cards slide to their new slots.
	const drawFrom = drawChipOrigin(scene, mount);
	let dealDelay = 0;
	for (const newIndex of added) {
		const card = hand[newIndex];
		const cell = new PIXI.Container();
		const plate = new PIXI.Graphics(); // persistent highlight plate (child 0)
		cell.addChild(plate);
		cell.alpha = 0;
		mount.strip.addChild(cell);

		const entry = {
			key: keys[newIndex], spot: `Card_${newIndex}`, data: card,
			cell, plate, baseY: handY, baseX: slotX(newIndex),
			w: cardW, h: cardH, lift: mount.lift,
			index: newIndex,
		};
		cell.zIndex = newIndex; // fan stacking: left under right
		nextCells[newIndex] = entry;
		wireCardInput(scene, entry);

		if (instant) {
			// Remount of an already-dealt hand: no theater, just be there.
			cell.x = entry.baseX;
			cell.y = handY;
			cell.alpha = 1;
		} else {
			if (drawFrom) {
				cell.x = drawFrom.x - cardW * 0.2;
				cell.y = drawFrom.y - cardH * 0.2;
				cell.scale.set(0.4);
			} else {
				cell.x = entry.baseX;
				cell.y = handY + 24;
			}
			// A multi-card refill (mulligan, opening hand) deals with a tiny stagger.
			gsap.to(cell, { alpha: 1, duration: 0.15, ease: 'none', delay: dealDelay });
			gsap.to(cell, { x: entry.baseX, y: handY, duration: 0.3, ease: 'power2.out', delay: dealDelay });
			gsap.to(cell.scale, { x: 1, y: 1, duration: 0.3, ease: 'power2.out', delay: dealDelay });
			dealDelay += 0.04;
		}

		builds.push(buildCardRender(scene, entry));
	}

	scene.handCells = nextCells;
	scene.handCards = nextCells.map((entry) => entry.cell);

	// If the row now fits on screen, glide the strip back home.
	if (mount.totalW <= mount.viewW - 20) {
		gsap.to(mount.strip, { x: 0, duration: 0.22, ease: 'power2.out' });
	}

	applyHandTargeting(scene);
	await Promise.all(builds);
}

/**
 * Render a card's PixiCard into its cell (child 1, above the plate). Shared
 * by the added path and the kept-path self-heal (a play-flight may have
 * flown the render away while a duplicate copy kept the cell).
 * @param {object} scene
 * @param {any} entry
 * @returns {Promise<void>}
 */
function buildCardRender(scene, entry) {
	const { cardH } = scene.handMount;
	return gen.PixiCard(entry.data, 0, 0, 'JustRender').then((cardEl) => {
		if (entry.cell.destroyed || entry.cell.children.length > 1) {
			cardEl.destroy({ children: true });
			return; // replaced (or already healed) while the art was loading
		}
		cardEl.scale.set(cardH / CARD_NATIVE_H);
		entry.cell.addChild(cardEl); // above the plate (child 0)
		// This render mounts AFTER applyHandTargeting ran (added cards build
		// async; the self-heal rebuilds child 1 too), so the "can't play this"
		// grayscale was applied to a child that didn't exist yet. Apply it now —
		// otherwise a freshly DRAWN card stays full-color while its identical,
		// already-in-hand copies are correctly grayed.
		applyCardGrayscale(cardEl, grayFor(entry.spot));
	}).catch((error) => {
		console.error('PixiCard failed for hand card', entry.data?.name, error);
	});
}

/**
 * Where a drawn card should fly in from: the Draw (deck) chip in your bar,
 * in hand-strip coordinates. Null when the bar isn't mounted yet.
 * @param {object} scene
 * @param {any} mount
 * @returns {{x:number, y:number}|null}
 */
function drawChipOrigin(scene, mount) {
	const chip = scene.barRefs?.drawChip;
	if (!chip || chip.destroyed) return null;
	const bounds = chip.getBounds();
	return mount.strip.toLocal(new PIXI.Point(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2));
}

/**
 * FEEDBACK pass: restyle every hand card for the current targeting state.
 * Mutates in place — no rebuilds, no flicker, and no alpha games (cards stay
 * fully opaque; the plates + motion carry the state).
 *
 * SELECTION is edge-triggered: the moment a card becomes selected it
 * SWOOSHES — slides left by 110% of the fan overlap (fully clearing the
 * neighbor that covered it), jumps to the top of the stack at the apex, and
 * swooshes back to its slot — while RISING into the reserved headroom above
 * the row. Deselecting settles it back down into fan order.
 * @param {object} scene
 * @returns {void}
 */
export function applyHandTargeting(scene) {
	if (!scene.handCells) return;
	const mount = scene.handMount;
	for (const entry of scene.handCells) {
		if (!entry || entry.cell.destroyed) continue;
		const highlight = highlightFor(entry.spot);
		const selected = highlight === 'selected';

		// v2 HIGHLIGHTS (highlight-fx.js): outline the card — teal = playable,
		// green = selected, orange = a legal target.
		applyHighlight(entry.cell, highlight);

		/* v1 BACKING PLATES — replaced by the outline filters above:
		killPulse(entry.plate);
		gsap.killTweensOf(entry.plate);
		entry.plate.clear();
		entry.plate.alpha = 1;
		if (highlight && highlight !== 'pickable') {
			const color = HIGHLIGHT_COLORS[highlight];
			entry.plate
				.roundRect(-5, -5, entry.w + 10, entry.h + 10, 12)
				.fill({ color, alpha: selected ? 0.95 : 0.8 });
			if (highlight === 'targeted') {
				// Solid plate + a gentle dip every 10s (no strobing).
				gentlePulse(entry.plate);
			}
		}
		*/
		entry.cell.alpha = 1;
		// Cards you can't act on right now: art 50% / frame+text 75% grayscale.
		applyCardGrayscale(entry.cell.children[1], grayFor(entry.spot));

		if (selected && !entry.wasSelected) {
			swooshSelect(entry, mount);
		} else if (!selected) {
			// Settle back into the fan (covers deselection AND mid-swoosh
			// cancels: x returns to the slot, y to the row, z to fan order).
			entry.cell.zIndex = entry.index;
			gsap.killTweensOf(entry.cell, 'x,y');
			gsap.to(entry.cell, {
				x: entry.baseX,
				y: entry.baseY,
				duration: 0.16,
				ease: 'power2.out',
				overwrite: 'auto',
			});
		}
		entry.wasSelected = selected;
	}
}

/**
 * The pick-up flourish: swoosh left 110% of the fan overlap, take the top of
 * the stack at the apex, swoosh back — rising the whole time.
 * @param {any} entry
 * @param {any} mount  scene.handMount (pitch = current fan spacing).
 */
function swooshSelect(entry, mount) {
	// Overlap = how much the right-hand neighbor covers this card. No fan
	// (full spacing) -> no sideways travel, just the rise + z pop.
	const overlap = Math.max(0, entry.w - (mount?.pitch ?? entry.w));
	const swooshX = overlap * 1.1;

	// Snappy and mechanical on purpose: short linear tweens, no easing.
	gsap.killTweensOf(entry.cell, 'x,y');
	gsap.timeline()
		.to(entry.cell, { x: entry.baseX - swooshX, duration: 0.05, ease: 'none' }, 0)
		.to(entry.cell, { y: entry.baseY - entry.lift, duration: 0.1, ease: 'none' }, 0)
		.call(() => { entry.cell.zIndex = 1000; }) // top of the fan at the apex
		.to(entry.cell, { x: entry.baseX, duration: 0.05, ease: 'none' });
}

/**
 * Tap = select/play via targeting; hold 250ms = zoom (like the board).
 * Handlers read entry.spot/entry.data LIVE — reconciliation renumbers spots
 * without rewiring anything. Dragging the strip suppresses the click.
 * @param {object} scene
 * @param {any} entry
 */
/** Mouse users get pre-click feedback (touch devices skip the listeners). */
const FINE_POINTER = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;

function wireCardInput(scene, entry) {
	const strip = scene.handMount.strip;
	entry.cell.eventMode = 'static';
	entry.cell.cursor = 'pointer';
	entry.cell.on('pointerdown', () => {
		if (!strip.dragging) scene.beginZoom(entry.data);
	});
	entry.cell.on('pointerup', () => {
		if (strip.dragged) return;       // it was a scroll, not a tap
		if (scene.endZoom()) return;     // the zoom consumed this press
		click(entry.spot);
	});
	entry.cell.on('pointerupoutside', () => scene.cancelZoom());
	if (FINE_POINTER) {
		// Desktop hover: peek the card up a touch. Only while nothing is
		// selected — selection owns the motion (swoosh/settle) then.
		entry.cell.on('pointerover', () => {
			if (targeting.selection || strip.dragging || !entry.cell.visible) return;
			gsap.to(entry.cell, { y: entry.baseY - 10, duration: 0.12, ease: 'power1.out', overwrite: 'auto' });
		});
		entry.cell.on('pointerout', () => {
			if (targeting.selection) return;
			gsap.to(entry.cell, { y: entry.baseY, duration: 0.15, ease: 'power1.out', overwrite: 'auto' });
		});
	}
}

/**
 * Simple horizontal drag scrolling for the hand strip. Installed once per
 * mount; reads the CURRENT row width off scene.handMount so reconciles don't
 * need to rewire it.
 * @param {object} scene
 * @param {PIXI.Container} strip
 */
function enableDragScroll(scene, strip) {
	strip.eventMode = 'static';
	let startPointerX = 0;
	let startStripX = 0;
	strip.dragging = false;
	strip.dragged = false;

	strip.on('pointerdown', (e) => {
		strip.dragging = true;
		strip.dragged = false;
		startPointerX = e.global.x;
		startStripX = strip.x;
	});
	const end = () => { strip.dragging = false; };
	strip.on('pointerup', end);
	strip.on('pointerupoutside', end);
	strip.on('globalpointermove', (e) => {
		const mount = scene.handMount;
		if (!strip.dragging || !mount || mount.totalW <= mount.viewW - 20) return;
		const dx = e.global.x - startPointerX;
		if (Math.abs(dx) > 8) {
			strip.dragged = true;
			scene.cancelZoom();
		}
		// Clamp so the strip can't be flung off past its edges.
		const minX = mount.viewW - mount.totalW - 20;
		strip.x = Math.max(Math.min(startStripX + dx, 20), minX);
	});
}
